import { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ChevronLeft, Mail, Phone, MessageSquare, Send } from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import emailjs from '@emailjs/browser';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const Contact = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const form = useRef();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.emailId || '',
    message: '',
  });

  const sendEmail = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await emailjs.sendForm(
        import.meta.env?.VITE_EMAILJS_SERVICE_ID,
        import.meta.env?.VITE_EMAILJS_TEMPLATE_ID,
        form.current,
        {
          publicKey: import.meta.env?.VITE_EMAILJS_API_KEY,
        }
      );
      toast.success('Message sent successfully! ✅');
      setFormData({ ...formData, message: '' });
    } catch (error) {
      console.error('Failed to send email:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col max-w-md mx-auto sm:border-l sm:border-r min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white border-b shadow-sm">
        <div className="container mx-auto px-4 py-4 flex items-center">
          <ChevronLeft
            onClick={() => navigate(-1)}
            className="h-6 w-6 cursor-pointer text-gray-600 hover:text-gray-900"
          />
          <h1 className="ml-3 text-xl font-semibold text-gray-900">Contact Us</h1>
        </div>
      </header>

      <main className="flex-grow container mx-auto pb-20 pt-6 px-4 space-y-6">
        {/* Quick Contact Options */}
        <div className="grid grid-cols-2 gap-4">
          <Card
            className="bg-white border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => window.open(`tel:+919899784200`)}
          >
            <CardContent className="p-4 text-center">
              <div className="bg-green-50 text-green-600 p-3 rounded-lg w-fit mx-auto mb-2">
                <Phone className="h-6 w-6" />
              </div>
              <h3 className="font-medium text-gray-900">Call Us</h3>
              <p className="text-sm text-gray-500">+91 9899784200</p>
            </CardContent>
          </Card>

          <Card
            className="bg-white border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => window.open(`https://wa.me/919899784200`)}
          >
            <CardContent className="p-4 text-center">
              <div className="bg-green-50 text-green-600 p-3 rounded-lg w-fit mx-auto mb-2">
                <MessageSquare className="h-6 w-6" />
              </div>
              <h3 className="font-medium text-gray-900">WhatsApp</h3>
              <p className="text-sm text-gray-500">Chat with us</p>
            </CardContent>
          </Card>
        </div>

        {/* Contact Form */}
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center text-lg">
              <Mail className="mr-2 h-5 w-5 text-green-600" />
              Send us a Message
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form ref={form} onSubmit={sendEmail} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  name="user_name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="user_email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  name="message"
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="How can we help you?"
                  required
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                <Send className="h-4 w-4 mr-2" />
                {loading ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Contact;
